export type ClueStatus =
  | "SUBMITTED"
  | "IN_REVIEW"
  | "SUPPLEMENT_REQUIRED"
  | "ACCEPTED_FOR_FOLLOW_UP"
  | "MERGED"
  | "NOT_ACCEPTED"
  | "WITHDRAWN";

export type ClueSummary = {
  id: string;
  publicRef: string;
  mergedIntoPublicRef?: string;
  status: ClueStatus;
  version: number;
  contentVersion: number;
  title: string;
  /// 社情民意 / 问题线索（重构方案 §4.4）。
  kind: "SOCIAL_CONDITION" | "ISSUE_CLUE";
  updatedAt: string;
  submittedAt: string;
  member: { displayName: string };
  organization: { id: string; name: string };
  task?: { id: string; title: string };
  receipt: { label: string; meaning: string; isAdoptionProof: false };
};

export type ClueDetail = ClueSummary & {
  content: {
    title: string;
    situation: string;
    suggestion: string;
    regionSummary: string;
    sourceType: string;
    contactPreference: string;
  };
  timeline: Array<{
    sequence: number;
    contentVersion: number;
    fromStatus?: ClueStatus | "DRAFT";
    toStatus: ClueStatus | "DRAFT";
    actor: "MEMBER" | "STAFF" | "SYSTEM";
    occurredAt: string;
    memberMessage?: string;
  }>;
};

export const CLUE_LABEL: Record<ClueStatus, string> = {
  SUBMITTED: "待受理",
  IN_REVIEW: "受理中",
  SUPPLEMENT_REQUIRED: "待会员补充",
  ACCEPTED_FOR_FOLLOW_UP: "进入后续跟进",
  MERGED: "已归并",
  NOT_ACCEPTED: "本轮不跟进",
  WITHDRAWN: "会员已撤回"
};

export const CLUE_TONE: Record<ClueStatus, "neutral" | "brand" | "success" | "warning"> = {
  SUBMITTED: "warning",
  IN_REVIEW: "brand",
  SUPPLEMENT_REQUIRED: "warning",
  ACCEPTED_FOR_FOLLOW_UP: "success",
  MERGED: "neutral",
  NOT_ACCEPTED: "neutral",
  WITHDRAWN: "neutral"
};

export const CLUE_FILTERS: Array<{ value: string; label: string }> = [
  { value: "SUBMITTED", label: "待受理" },
  { value: "IN_REVIEW", label: "受理中" },
  { value: "SUPPLEMENT_REQUIRED", label: "待补充" },
  { value: "ACCEPTED_FOR_FOLLOW_UP", label: "后续跟进" },
  { value: "NOT_ACCEPTED", label: "本轮不跟进" },
  { value: "", label: "全部" }
];

export type CallStatus = "PUBLISHED" | "CLOSED" | "WITHDRAWN";
export type CallSummary = {
  id: string;
  status: CallStatus;
  version: number;
  category: string;
  title: string;
  targetLabel?: string;
  opensAt: string;
  closesAt?: string;
  publishedAt: string;
  organization: { id: string; name: string };
  // 受众是发布那一刻冻结的收件人快照；应征数按建言收件事实实时统计。
  audienceCount: number;
  responseCount: number;
  acceptedCount: number;
  updatedAt: string;
};

export type CallDetail = CallSummary & {
  description: string;
  requirements: string[];
  withdrawnReason?: string;
  timeline: Array<{
    sequence: number;
    fromStatus?: CallStatus;
    toStatus: CallStatus;
    occurredAt: string;
    actorAccountId?: string;
    reasonCode?: string;
    note?: string;
  }>;
  // 应征材料就是建言收件记录，不另建一套；这里只给分诊需要的摘要。
  responses: Array<{
    submissionId: string;
    publicRef: string;
    title: string;
    memberDisplayName: string;
    status: string;
    submittedAt?: string;
    updatedAt: string;
  }>;
};
export const CALL_LABEL: Record<CallStatus, string> = {
  PUBLISHED: "征集中",
  CLOSED: "已截止",
  WITHDRAWN: "已撤回"
};

export const CALL_TONE: Record<CallStatus, "brand" | "neutral" | "warning"> = {
  PUBLISHED: "brand",
  CLOSED: "neutral",
  WITHDRAWN: "warning"
};

export const CALL_FILTERS = [
  { value: "PUBLISHED", label: "征集中" },
  { value: "CLOSED", label: "已截止" },
  { value: "WITHDRAWN", label: "已撤回" },
  { value: "", label: "全部" }
];

export const CATEGORIES = ["大会发言材料", "界别发言材料", "社情民意专项", "提案线索", "长期征集"];

export type TopicStatus = "PROPOSED" | "APPROVED" | "ACTIVE" | "ON_HOLD" | "COMPLETED" | "CLOSED";

export type TopicSummary = {
  id: string;
  publicRef: string;
  title: string;
  direction: string;
  year: number;
  status: TopicStatus;
  version: number;
  leadLabel: string;
  organization: { id: string; name: string };
  disposition?: TopicDisposition;
  clueCount: number;
  manuscriptCount: number;
  submissionCount: number;
  updatedAt: string;
  // 报送 ≠ 采用：本字段只统计已登记的外部反馈条数，不代表被采纳。
  outcomeCount: number;
};

export type TopicSubmissionView = {
  id: string;
  publicRef: string;
  version: number;
  targetLabel: string;
  docType: string;
  status: TopicSubmissionStatus;
  manuscriptVersionNumber: number;
  submittedAt?: string;
  outcomes: Array<{
    kind: TopicOutcomeKind;
    occurredAt?: string;
    evidenceRef?: string;
    note: string;
    recordedAt: string;
  }>;
};

export type TopicDetail = TopicSummary & {
  expertLabels: string[];
  clueLinks: Array<{
    clueSubmissionId: string;
    publicRef: string;
    title: string;
    memberDisplayName: string;
    linkedAt: string;
    note?: string;
  }>;
  fieldTrips: Array<{ occurredAt: string; place: string; note: string }>;
  manuscripts: Array<{
    versionNumber: number;
    title: string;
    docType: string;
    summary: string;
    authorLabel: string;
    aiAssisted: boolean;
    recordedAt: string;
  }>;
  submissions: TopicSubmissionView[];
  timeline: Array<{
    sequence: number;
    fromStatus?: TopicStatus;
    toStatus: TopicStatus;
    occurredAt: string;
    reasonCode?: string;
    note?: string;
  }>;
};
export const TOPIC_LABEL: Record<TopicStatus, string> = {
  PROPOSED: "已提出",
  APPROVED: "已立项",
  ACTIVE: "调研中",
  ON_HOLD: "暂缓",
  COMPLETED: "已完成",
  CLOSED: "已结项"
};

export const TOPIC_TONE: Record<TopicStatus, "neutral" | "brand" | "success" | "warning"> = {
  PROPOSED: "neutral",
  APPROVED: "brand",
  ACTIVE: "brand",
  ON_HOLD: "warning",
  COMPLETED: "success",
  CLOSED: "neutral"
};

// 报送记录的状态由服务端给（prisma enum TopicSubmissionStatus），这里只负责说人话。
// 四个值全在这里，认不出的原样显示。注意「已回执」不等于「被采纳」——
// 回执只说明对方反馈了什么，采纳与否记在 outcomes 里。
export const SUBMISSION_LABEL: Record<string, string> = {
  PREPARED: "待报送",
  SUBMITTED: "已报送",
  ACKNOWLEDGED: "已回执",
  NO_RESPONSE: "未获反馈"
};

// 课题状态只能按这张图走；界面上只给当前状态允许的下一步。
export const NEXT_STATUS: Record<TopicStatus, TopicStatus[]> = {
  PROPOSED: ["APPROVED", "CLOSED"],
  APPROVED: ["ACTIVE", "ON_HOLD", "CLOSED"],
  ACTIVE: ["COMPLETED", "ON_HOLD", "CLOSED"],
  ON_HOLD: ["ACTIVE", "CLOSED"],
  COMPLETED: ["CLOSED"],
  CLOSED: []
};

export const TOPIC_FILTERS = [
  { value: "ACTIVE", label: "调研中" },
  { value: "APPROVED", label: "已立项" },
  { value: "PROPOSED", label: "已提出" },
  { value: "COMPLETED", label: "已完成" },
  { value: "", label: "全部" }
];

export const DOC_TYPES = ["社情民意信息", "提案", "大会发言", "调研报告", "建议案"];

export type TopicDisposition = "TRANSFER_TRACKING" | "CONTINUE_TRACKING" | "CLOSED";
export type TopicSubmissionStatus = "PREPARED" | "SUBMITTED" | "ACKNOWLEDGED" | "NO_RESPONSE";
export type TopicOutcomeKind =
  | "FORWARDED"
  | "ADOPTED"
  | "INSTRUCTED"
  | "HANDLED"
  | "IMPLEMENTED"
  | "NOT_ADOPTED"
  | "NOT_PASSED"
  | "AWAITING";
export const OUTCOME_LABEL: Record<string, string> = {
  FORWARDED: "转送",
  ADOPTED: "采用",
  INSTRUCTED: "批示",
  HANDLED: "办理",
  IMPLEMENTED: "实施",
  NOT_ADOPTED: "未采用",
  NOT_PASSED: "未通过",
  AWAITING: "等待反馈"
};
export const DISPOSITION_LABEL = {
  TRANSFER_TRACKING: "转入转化跟踪",
  CONTINUE_TRACKING: "继续跟踪",
  CLOSED: "关闭事项"
};
export type ParticipationKind = "clue" | "call" | "topic";
export type Summary = ClueSummary | CallSummary | TopicSummary;
export type Detail = ClueDetail | CallDetail | TopicDetail;
export const ENDPOINT = {
  clue: "/api/v1/clue-intake",
  call: "/api/v1/collection-tasks",
  topic: "/api/v1/topics"
};
export const TITLES = { clue: "建言受理", call: "材料征集", topic: "课题与报送" };
export type ClueAction =
  "start-review" | "request-supplement" | "accept-for-follow-up" | "not-accept" | "merge";
export type Editor =
  | { kind: "clue"; action: ClueAction; record: ClueDetail }
  | { kind: "call"; action: "create" | "close" | "withdraw"; record: CallDetail | null }
  | {
      kind: "topic";
      action: "create" | "status" | "manuscript" | "submission" | "link" | "trip" | "no-response";
      record: TopicDetail | null;
      nextStatus?: TopicStatus;
      submission?: TopicSubmissionView;
      versionNumber?: number;
      source?: ClueDetail;
    };
export const CLUE_ACTION_LABEL: Record<ClueAction, string> = {
  "start-review": "开始受理",
  "request-supplement": "请会员补充",
  "accept-for-follow-up": "进入后续跟进",
  "not-accept": "本轮不跟进",
  merge: "归并建言"
};
export function localNow(days = 0) {
  const date = new Date(Date.now() + days * 86400000);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

export const CLUE_KIND_LABEL: Record<ClueSummary["kind"], string> = {
  SOCIAL_CONDITION: "社情民意",
  ISSUE_CLUE: "问题线索"
};
